import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import type { ColDef } from 'ag-grid-community';
import { Activity, AlertTriangle, CheckCircle, Clock, Database, Download, Plus, Shield } from 'lucide-react';
import {
  AxiomActionBar,
  AxiomButton,
  AxiomDateField,
  AxiomEmptyState,
  AxiomFieldLabel,
  AxiomInput,
  AxiomMetricCard,
  AxiomMetricGrid,
  AxiomModal,
  AxiomSelect,
  AxiomStatusBadge,
  AxiomTextArea,
  AxiomTimeline,
  AxiomTimelineItem,
  InstitutionalDataGrid,
  ObjectDetailDrawer,
} from './shared';
import { axiomTheme } from '../styles/designTokens';

const Page = styled.div`
  min-height: calc(100vh - 64px);
  padding: ${axiomTheme.spacing.page};
  color: ${axiomTheme.color.text.secondary};
`;

const Hero = styled.div`
  border: 1px solid ${axiomTheme.color.border.default};
  border-radius: ${axiomTheme.radius.hero};
  background: ${axiomTheme.surface.hero};
  box-shadow: ${axiomTheme.shadow.panel};
  padding: 1.25rem;
  margin-bottom: 1rem;
`;

const Eyebrow = styled.div`
  color: ${axiomTheme.color.tone.cyan};
  font-size: ${axiomTheme.typography.eyebrow.size};
  font-weight: ${axiomTheme.typography.eyebrow.weight};
  letter-spacing: ${axiomTheme.typography.eyebrow.tracking};
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 0.35rem 0 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 1.55rem;
  font-weight: 850;
  letter-spacing: -0.03em;
`;

const Subtitle = styled.div`
  color: ${axiomTheme.color.text.muted};
  margin-top: 0.45rem;
  max-width: 900px;
  line-height: 1.55;
`;

const Section = styled.section`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xxl};
  background: ${axiomTheme.surface.panel};
  margin-top: 1rem;
  overflow: hidden;
`;

const SectionHeader = styled.div`
  padding: 0.95rem 1rem;
  border-bottom: 1px solid ${axiomTheme.color.border.subtle};
  background: ${axiomTheme.surface.panelHeader};
`;

const SectionTitle = styled.h2`
  margin: 0;
  color: ${axiomTheme.color.text.primary};
  font-size: 0.95rem;
  font-weight: 850;
`;

const SectionBody = styled.div`
  padding: 1rem;
`;

const SwatchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.75rem;
`;

const Swatch = styled.div<{ $color: string }>`
  border: 1px solid ${axiomTheme.color.border.subtle};
  border-radius: ${axiomTheme.radius.xl};
  background: rgba(15, 23, 42, 0.55);
  overflow: hidden;

  &::before {
    content: '';
    display: block;
    height: 42px;
    background: ${p => p.$color};
  }
`;

const SwatchLabel = styled.div`
  padding: 0.65rem;
  color: ${axiomTheme.color.text.secondary};
  font-size: 0.72rem;
  font-weight: 800;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.85rem;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
`;

const sampleRows = [
  { id: 1, object: 'IBOR Position', owner: 'PM', status: 'PASS', exposure: 12500000, workflow: 'Position drilldown' },
  { id: 2, object: 'ABF Facility', owner: 'Risk', status: 'WARNING', exposure: 90000000, workflow: 'Borrowing-base review' },
  { id: 3, object: 'Document Job', owner: 'Ops', status: 'BREACH', exposure: 0, workflow: 'Booking hold' },
];

const DesignSystemScreen: React.FC = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const columns = useMemo<ColDef[]>(() => [
    { field: 'object', headerName: 'Object', pinned: 'left', minWidth: 180 },
    { field: 'owner', headerName: 'Owner', width: 110 },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      cellRenderer: (p: any) => (
        <AxiomStatusBadge $tone={p.value === 'PASS' ? 'emerald' : p.value === 'WARNING' ? 'amber' : 'red'}>
          {p.value}
        </AxiomStatusBadge>
      ),
    },
    {
      field: 'exposure',
      headerName: 'Exposure',
      width: 150,
      valueFormatter: (p) => `$${Number(p.value || 0).toLocaleString()}`,
      cellStyle: { textAlign: 'right', fontWeight: '700' } as any,
    },
    { field: 'workflow', headerName: 'Workflow', minWidth: 210 },
  ], []);

  return (
    <Page>
      <Hero>
        <Eyebrow>Axiom Design QA</Eyebrow>
        <Title>Institutional UI Contract</Title>
        <Subtitle>
          Canonical controls for data-dense credit workflows. New screens should compose from these primitives before
          adding local styled-components.
        </Subtitle>
      </Hero>

      <Section>
        <SectionHeader><SectionTitle>Color, Tone & Status</SectionTitle></SectionHeader>
        <SectionBody>
          <SwatchGrid>
            <Swatch $color={axiomTheme.color.tone.cyan}><SwatchLabel>Cyan / Primary Data</SwatchLabel></Swatch>
            <Swatch $color={axiomTheme.color.tone.emerald}><SwatchLabel>Emerald / Pass</SwatchLabel></Swatch>
            <Swatch $color={axiomTheme.color.tone.amber}><SwatchLabel>Amber / Warning</SwatchLabel></Swatch>
            <Swatch $color={axiomTheme.color.tone.red}><SwatchLabel>Red / Breach</SwatchLabel></Swatch>
            <Swatch $color={axiomTheme.color.tone.violet}><SwatchLabel>Violet / Alternatives</SwatchLabel></Swatch>
            <Swatch $color={axiomTheme.color.panelMuted}><SwatchLabel>Muted Surface</SwatchLabel></Swatch>
          </SwatchGrid>
          <ButtonRow style={{ marginTop: '1rem' }}>
            <AxiomStatusBadge $tone="emerald"><CheckCircle size={13} /> Passed</AxiomStatusBadge>
            <AxiomStatusBadge $tone="amber"><Clock size={13} /> Warning</AxiomStatusBadge>
            <AxiomStatusBadge $tone="red"><AlertTriangle size={13} /> Breached</AxiomStatusBadge>
            <AxiomStatusBadge $tone="cyan"><Shield size={13} /> Approved</AxiomStatusBadge>
          </ButtonRow>
        </SectionBody>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>Buttons, Inputs & Form Grammar</SectionTitle></SectionHeader>
        <SectionBody>
          <ButtonRow>
            <AxiomButton $variant="primary"><Plus /> Primary Action</AxiomButton>
            <AxiomButton $variant="secondary"><Download /> Secondary</AxiomButton>
            <AxiomButton $variant="danger"><AlertTriangle /> Breach Action</AxiomButton>
            <AxiomButton $variant="quiet">Quiet</AxiomButton>
            <AxiomButton $variant="ghost">Ghost</AxiomButton>
          </ButtonRow>
          <FormGrid style={{ marginTop: '1rem' }}>
            <div>
              <AxiomFieldLabel>Portfolio</AxiomFieldLabel>
              <AxiomSelect defaultValue="credit">
                <option value="credit">Credit Opportunities</option>
                <option value="alts">Alternatives Sleeve</option>
              </AxiomSelect>
            </div>
            <div>
              <AxiomFieldLabel>As-of Date</AxiomFieldLabel>
              <AxiomDateField defaultValue="2026-06-09" />
            </div>
            <div>
              <AxiomFieldLabel>Search</AxiomFieldLabel>
              <AxiomInput placeholder="Search object, issuer, CUSIP..." />
            </div>
            <div>
              <AxiomFieldLabel>Notes</AxiomFieldLabel>
              <AxiomTextArea placeholder="Audit note or override rationale..." />
            </div>
          </FormGrid>
        </SectionBody>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>Metrics, Empty State & Timeline</SectionTitle></SectionHeader>
        <SectionBody>
          <AxiomMetricGrid>
            <AxiomMetricCard label="Exposure" value="$1.24B" subtext="IBOR-sourced market value" icon={<Database />} tone="cyan" />
            <AxiomMetricCard label="Breaches" value="2" subtext="Requires Risk review" icon={<AlertTriangle />} tone="red" />
            <AxiomMetricCard label="Ops Queue" value="14" subtext="Settlement, docs and recon" icon={<Activity />} tone="amber" />
          </AxiomMetricGrid>
          <AxiomTimeline style={{ marginTop: '1rem' }}>
            <AxiomTimelineItem $tone="emerald">POSTED trade created IBOR position.</AxiomTimelineItem>
            <AxiomTimelineItem $tone="amber">Document booking hold requires Ops review.</AxiomTimelineItem>
          </AxiomTimeline>
          <AxiomEmptyState style={{ marginTop: '1rem' }}>
            Empty states use dashed institutional panels, not marketing cards.
          </AxiomEmptyState>
        </SectionBody>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>Institutional Data Grid</SectionTitle></SectionHeader>
        <SectionBody>
          <InstitutionalDataGrid
            title="Component QA grid"
            subtitle="Saved views, column chooser, density, export, pinned totals and row-click drawer."
            rowData={sampleRows}
            columnDefs={columns}
            gridId="design-system-qa"
            height={330}
            pinnedBottomRowData={[{ id: 99, object: 'TOTAL', owner: '', status: 'PASS', exposure: 102500000, workflow: '' }]}
            onRowClicked={() => setDrawerOpen(true)}
          />
        </SectionBody>
      </Section>

      <Section>
        <SectionHeader><SectionTitle>Drawer & Modal</SectionTitle></SectionHeader>
        <SectionBody>
          <AxiomActionBar style={{ justifyContent: 'flex-start' }}>
            <AxiomButton $variant="primary" onClick={() => setDrawerOpen(true)}>Open Drawer</AxiomButton>
            <AxiomButton $variant="secondary" onClick={() => setModalOpen(true)}>Open Modal</AxiomButton>
          </AxiomActionBar>
        </SectionBody>
      </Section>

      <ObjectDetailDrawer
        open={drawerOpen}
        title="Unified Object Detail"
        subtitle="Shared shell for positions, loans, CLOs, ABF, PE funds, covenants and documents."
        status="Active"
        assetClass="Design QA"
        onClose={() => setDrawerOpen(false)}
        identifiers={[
          { label: 'Object ID', value: 'QA-001' },
          { label: 'Owner', value: 'Platform' },
          { label: 'As-of', value: 'Jun 9, 2026' },
        ]}
        metrics={[
          { label: 'Economics', value: '$125M', tone: 'cyan' },
          { label: 'Risk', value: 'Pass', tone: 'emerald' },
        ]}
        sections={[
          { title: 'Lifecycle Events', eyebrow: 'Standard Slot', content: <div>Events, docs, cash, audit and related trades live in consistent drawer sections.</div> },
        ]}
      />

      <AxiomModal
        open={modalOpen}
        title="Standard Modal"
        onClose={() => setModalOpen(false)}
        actions={<AxiomButton $variant="primary" onClick={() => setModalOpen(false)}>Confirm</AxiomButton>}
      >
        <div style={{ color: axiomTheme.color.text.muted, fontSize: '0.82rem', lineHeight: 1.55 }}>
          Modals use the same borders, radius, backdrop, typography and action grammar as drawers and forms.
        </div>
      </AxiomModal>
    </Page>
  );
};

export default DesignSystemScreen;

